/**
 * Runs: the history, one row per AI call in the period (a month or a
 * year), under the filters it shares with Breakdown. Every column sorts
 * (newest first by default) and the list pages on the server. Times read
 * in local time or UTC, the app-wide choice shared with the audit log.
 */

import { useState } from 'react';
import { FileText, Gauge } from 'lucide-react';

import { useRuns } from '@/api/queries';
import type { AiRun, Period, Usage } from '@/api/types';
import { formatDateTime, formatInt, formatUsd } from '@/ui/format';
import { Pagination } from '@/ui/Pagination';
import { SortHeader } from '@/ui/SortHeader';
import { LoadFailure } from '@/ui/StateCard';
import { useTimeZone } from '@/ui/useTimeZone';

import { RunErrorDialog } from './RunErrorDialog';
import { RunFilterBar, isFiltered, useMatchingParams, type RunFilters } from './RunFilterBar';
import { SkeletonRows } from './SkeletonRows';
import { formatDuration, sortPatch, statusStyle } from './usageLists';
import { periodLabel } from './usageUi';

export interface RunsView {
  sort: string;
  order: 'asc' | 'desc';
  page: number;
  page_size: number;
}

export const DEFAULT_RUNS: RunsView = { sort: 'created_at', order: 'desc', page: 1, page_size: 50 };

// Text columns read A–Z on the first click; numbers and times biggest first.
const ASCENDING_FIRST = new Set(['process', 'model', 'user', 'status']);

const COLUMNS = 13;

export function RunsTab({
  period,
  data,
  filters,
  onFilters,
  view,
  onView,
}: {
  period: Period;
  data: Usage;
  filters: RunFilters;
  onFilters: (patch: Partial<RunFilters>) => void;
  view: RunsView;
  onView: (patch: Partial<RunsView>) => void;
}) {
  const matching = useMatchingParams(period, filters);
  const runs = useRuns({ ...matching, ...view });
  const total = runs.data?.total ?? 0;
  const processLabel = new Map(data.by_process.map((p) => [p.key, p.label]));
  const labelOf = (key: string) => processLabel.get(key) ?? key;
  const [inspecting, setInspecting] = useState<AiRun | null>(null);
  const [utc, setUtc] = useTimeZone();

  // A picked day follows the choice, as on the audit log.
  function chooseZone(next: boolean) {
    setUtc(next);
    if (filters.when.kind !== 'preset' || filters.when.preset !== 'all') onFilters({ whenUtc: next });
  }

  return (
    <>
      <RunFilterBar period={period} data={data} filters={filters} onChange={onFilters} utc={utc} trail={<ZoneSwitch utc={utc} onChange={chooseZone} />} />

      {runs.error ? (
        <LoadFailure error={runs.error} onRetry={() => void runs.refetch()} />
      ) : (
        <>
          <RunsTable
            period={period}
            rows={runs.data?.runs}
            pending={runs.isPending}
            filtered={isFiltered(filters)}
            utc={utc}
            view={view}
            onView={onView}
            labelOf={labelOf}
            onInspect={setInspecting}
          />
          {total > 0 && (
            <Pagination
              page={view.page}
              pageSize={view.page_size}
              total={total}
              noun="runs"
              onPage={(page) => onView({ page })}
              onPageSize={(page_size) => onView({ page_size, page: 1 })}
            />
          )}
        </>
      )}
      {inspecting && (
        <RunErrorDialog run={inspecting} processLabel={labelOf(inspecting.process_key)} utc={utc} onClose={() => setInspecting(null)} />
      )}
    </>
  );
}

function ZoneSwitch({ utc, onChange }: { utc: boolean; onChange: (utc: boolean) => void }) {
  return (
    <div className="segmented" role="radiogroup" aria-label="Show times in">
      <button type="button" role="radio" aria-checked={!utc} onClick={() => onChange(false)}>Local</button>
      <button type="button" role="radio" aria-checked={utc} onClick={() => onChange(true)}>UTC</button>
    </div>
  );
}

function RunsTable({
  period,
  rows = [],
  pending,
  filtered,
  utc,
  view,
  onView,
  labelOf,
  onInspect,
}: {
  period: Period;
  rows?: AiRun[];
  pending: boolean;
  filtered: boolean;
  utc: boolean;
  view: RunsView;
  onView: (patch: Partial<RunsView>) => void;
  labelOf: (processKey: string) => string;
  onInspect: (run: AiRun) => void;
}) {
  const onSort = (key: string) => onView(sortPatch(view, key, (k) => ASCENDING_FIRST.has(k)));
  const sortProps = { sort: view.sort, order: view.order, onSort };
  return (
    <div className="table-scroll">
      <table className="data zebra compact">
        <thead>
          <tr>
            <SortHeader label={utc ? 'When (UTC)' : 'When'} sortKey="created_at" {...sortProps} />
            <SortHeader label="Process" sortKey="process" {...sortProps} />
            <SortHeader label="Model" sortKey="model" {...sortProps} />
            <SortHeader label="Who" sortKey="user" {...sortProps} />
            <SortHeader label="In" sortKey="tokens_input" className="num" {...sortProps} />
            <SortHeader label="Out" sortKey="tokens_output" className="num" {...sortProps} />
            <SortHeader label="Cached" sortKey="tokens_cache_read" className="num" {...sortProps} />
            <SortHeader label="Duration" sortKey="latency_ms" className="num" {...sortProps} />
            <SortHeader label="Turns" sortKey="turns" className="num" {...sortProps} />
            <SortHeader label="Tool calls" sortKey="tool_calls" className="num" {...sortProps} />
            <SortHeader label="Cost" sortKey="cost_usd" className="num" {...sortProps} />
            <SortHeader label="Status" sortKey="status" {...sortProps} />
            <th scope="col"><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {pending ? (
            <SkeletonRows columns={COLUMNS} />
          ) : rows.length === 0 ? (
            <EmptyRow period={period} filtered={filtered} />
          ) : (
            rows.map((run) => (
              <RunRow key={run.id} run={run} processLabel={labelOf(run.process_key)} utc={utc} onInspect={() => onInspect(run)} />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function EmptyRow({ period, filtered }: { period: Period; filtered: boolean }) {
  return (
    <tr>
      <td colSpan={COLUMNS}>
        <div className="empty">
          <div className="e-icon"><Gauge aria-hidden="true" /></div>
          {filtered ? (
            <>
              <h3>No runs match</h3>
              <p>Clear the filters to see the rest of {periodLabel(period)}.</p>
            </>
          ) : (
            <>
              <h3>No AI calls in {periodLabel(period)}</h3>
              <p>Runs appear here as modules call the AI layer, including connection tests from the AI models screen.</p>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

function RunRow({ run, processLabel, utc, onInspect }: { run: AiRun; processLabel: string; utc: boolean; onInspect: () => void }) {
  const status = statusStyle(run.status);
  return (
    <tr>
      <td className="nowrap" title={run.created_at}>{formatDateTime(run.created_at, utc)}</td>
      <td>{processLabel}</td>
      <td>{run.model_name ?? 'Not recorded'}</td>
      <td className={run.user_name ? undefined : 'hint'}>{run.user_name ?? 'System'}</td>
      <td className="num">{formatInt(run.tokens_input)}</td>
      <td className="num">{formatInt(run.tokens_output)}</td>
      <td className="num">{formatInt(run.tokens_cache_read)}</td>
      <td className="num">{formatDuration(run.latency_ms)}</td>
      <td className="num">{formatInt(run.turns)}</td>
      <td className="num">{formatInt(run.tool_calls)}</td>
      <td className="num">{formatUsd(run.cost_usd)}</td>
      <td>
        <span className={`badge ${status.className}`}>{status.label}</span>
      </td>
      <td>
        {run.error && (
          <div className="row-actions">
            <button
              type="button"
              className="btn btn-secondary btn-icon"
              data-tooltip="Show error"
              aria-label={`Show the error of the ${formatDateTime(run.created_at, utc)} run`}
              onClick={onInspect}
            >
              <FileText aria-hidden="true" />
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}
